<template>
  <div>
    <Teleport
      v-if="useAuthStore().isSuperAdmin || hasMultipleWorkspaces"
      defer
      to="#header-slot"
    >
      <div
        v-if="!loadingProfile"
        class="flex items-center gap-1 w-[400px]"
      >
        <div class="h-4 min-w-1 rounded-full bg-primary" />
        <p class="text-xs font-medium ps-1 mr-3 text-nowrap uppercase text-sidebar-foreground/70">
          Workspace
        </p>
        <CCombobox
          v-model="currentWorkspaceID"
          placeholder="Search workspaces..."
          search-placeholder="Search workspaces..."
          empty-text="No workspaces found."
          :options="workspacesList"
          :loading="loadingWorkspaces"
          :remote="useAuthStore().isSuperAdmin"
          @search="onWorkspaceSearch"
          @update:model-value="changeWorkspace"
        />
      </div>
    </Teleport>

    <!-- One shared workspace header for every page under this layout. -->
    <WorkspaceContextCard v-if="!(isDashboardRoute && isAuditAssignee)" />
    <section class="mt-4">
      <RouterView />
    </section>

    <!-- Workspace-wide floating assistant (all modules under this layout) -->
    <Chat
      v-if="chatWorkspaceId && !isAuditAssignee"
      :workspace-id="chatWorkspaceId"
      :workspace-name="selectedWorkspace?.name ?? ''"
    />

    <AddUserDialog
      v-if="userDialogRequested"
      ref="userDialog"
      @success="assignOfficer"
    />
    <CDialog
      ref="descriptionDialog"
      v-slot="{ hide }"
      title="Update Workspace Description"
      description="Edit and save the workspace description."
      hide-footer
      content-class="sm:max-w-xl"
      @hide="resetDescriptionForm"
    >
      <div class="space-y-3">
        <QuillEditor
          v-model:content="descriptionForm"
          content-type="html"
          toolbar="full"
          theme="snow"
        />
        <div class="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            :disabled="savingDescription"
            @click="hide"
          >
            Cancel
          </Button>
          <Button
            type="button"
            :loading="savingDescription"
            @click="updateDescription(hide)"
          >
            Save Description
          </Button>
        </div>
      </div>
    </CDialog>
  </div>
</template>
<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore, useWorkspaceStore } from "@/stores/index.js";
import { Button } from "@/components/ui/button/index.js";
import WorkspaceContextCard from "@/components/workspace/WorkspaceContextCard.vue";
import CCombobox from "@/components/common/form/CCombobox.vue";
import CDialog from "@/components/common/CDialog.vue";
import { useAdminWorkspaces } from '@/queries/workspaces'
import { invalidateList } from '@/queries/storeCache'

// Lazy-load so the assistant chunk is not in the initial workspace bundle.
const Chat = defineAsyncComponent(() => import('@/components/Chat.vue'))
// Lazy too: the dialog (and the user/role/department queries it loads) is only needed once
// someone assigns a compliance officer, so it stays out of the entry bundle until then.
const AddUserDialog = defineAsyncComponent(() => import('@/components/users/AddUserDialog.vue'))

const SEARCH_DEBOUNCE_MS = 300

const route = useRoute();
const router = useRouter();

const authStore = useAuthStore()
const workspacesStore = useWorkspaceStore()
const { workspaces: profileWorkspaces, hasMultipleWorkspaces, selectedWorkspace: selectedWorkspaceRef } = storeToRefs(workspacesStore)
const { loadingProfile } = storeToRefs(authStore)
// The `workspace.store.js` state initializes these as `[]`/`null` with no type
// annotation, so plain destructuring infers `never[]`/`never`. Re-wrap as `any`.
// Super admins switch between every workspace (searchable, from the API); everyone else between
// the ones that came with their profile.
const workspaceSearch = ref('')
const { workspaces: adminWorkspaces, isFetching: loadingWorkspaces } = useAdminWorkspaces(
  computed(() => ({ search: workspaceSearch.value || undefined })),
)
const workspaces = computed<any[]>(() =>
  authStore.isSuperAdmin ? adminWorkspaces.value : profileWorkspaces.value,
)
const selectedWorkspace = computed<any>(() => selectedWorkspaceRef.value)
const currentWorkspaceID = ref(Number(route.params.workspace_id) || selectedWorkspace.value?.id)
const chatWorkspaceId = computed(
  () => route.params.workspace_id ?? workspacesStore.getWorkspaceID,
)
const isAuditAssignee = computed(() => authStore.isAuditAssignee)
const isDashboardRoute = computed(() => route.name === 'workspaces-dashboard')
const userDialog = ref<any>(null)
const descriptionDialog = ref<any>(null)
const descriptionForm = ref('')
const savingDescription = ref(false)
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null

const workspacesList = computed(() => {
  const list = workspaces.value.map((ws: any) => ({
    label: ws.name,
    value: ws.id,
  }))
  // Keep the active workspace visible when it falls outside the search results.
  const selected = selectedWorkspace.value
  if (selected?.id && !list.some(opt => opt.value === selected.id)) {
    list.unshift({ label: selected.name, value: selected.id })
  }
  return list
})

const onWorkspaceSearch = (query: string) => {
  // Shared-workspace users filter client-side via CCombobox; only super admins hit the API.
  if (!useAuthStore().isSuperAdmin) return

  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
  searchDebounceTimer = setTimeout(() => {
    const nextSearch = (query ?? '').trim()
    if (workspaceSearch.value === nextSearch) return
    workspaceSearch.value = nextSearch
  }, SEARCH_DEBOUNCE_MS)
}

/** Restore the full list after a filtered selection (or any active search). */
const clearWorkspaceSearch = () => {
  if (!useAuthStore().isSuperAdmin || !workspaceSearch.value) return
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
  workspaceSearch.value = ''
}

const changeWorkspace = (id: number | string) => {
  if (!id) return
  clearWorkspaceSearch()
  router.push(`/workspaces/${id}`)
  nextTick(() => {
    getWorkspace(id)
    useAuthStore().GetProfile({workspace_id: id}, { silent: true })
  })
}

// `id` mirrors workspace.store.js's own `GetWorkspace(id = undefined)`, which
// falls back to the current workspace — callers below rely on that fallback.
const getWorkspace = (id?: number | string) => {
  // workspace.store.js declares `GetWorkspace(id = undefined)` with no JSDoc,
  // so TS infers the param type as literally `undefined` — cast to bypass that.
  workspacesStore.GetWorkspace(id as any)
}

// Mounted on first use; it opens as soon as its async chunk has loaded and the ref is set.
const userDialogRequested = ref(false)
let openUserDialogWhenReady = false
watch(userDialog, (dialog) => {
  if (dialog && openUserDialogWhenReady) {
    openUserDialogWhenReady = false
    dialog.show()
  }
})

const showUserDialog = () => {
  if (userDialog.value) {
    userDialog.value.show()
    return
  }
  openUserDialogWhenReady = true
  userDialogRequested.value = true
}

const openDescriptionDialog = () => {
  descriptionForm.value = selectedWorkspace.value?.description ?? ''
  descriptionDialog.value?.show()
}

const redirectToSettings = () => {
  router.push(`/workspaces/${currentWorkspaceID.value}/workspace-settings`)
}

// WorkspaceContextCard reuses these actions.
provide('workspaceHeaderActions', {
  openDescriptionDialog,
  showUserDialog,
  redirectToSettings,
})
const resetDescriptionForm = () => {
  descriptionForm.value = selectedWorkspace.value?.description ?? ''
  savingDescription.value = false
}

const updateDescription = async (hide: () => void) => {
  savingDescription.value = true
  try {
    await workspacesStore.UpdateWorkspaceDescription({
      description: descriptionForm.value,
    })
    await getWorkspace()
    hide()
  } finally {
    savingDescription.value = false
  }
}

const assignOfficer = () => {
  getWorkspace()
  invalidateList('workspaces')
}

onMounted(() => {
  getWorkspace()
})

onBeforeUnmount(() => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
})
</script>
